<template>
  <div class="create-order">
    <div class="flex items-center bg-neutral-700 py-10 px-40 stepper relative mdmax:px-4 mdmax:text-center mdmax:gap-2 mdmax:-mx-4 mdmax:py-6 mdmax:items-stretch">
      <div class="flex-1 py-2 px-6 rounded bg-neutral-600 mdmax:pl-3 mdmax:pr-3" :class="{ arrow: !isMobile }">
        <p class="text-sm font-medium text-white">{{ $t('order.createOrder.stepOne.title') }}</p>
        <p class="text-xs text-white">{{ $t('order.createOrder.stepOne.desc') }}</p>
      </div>
      <div class="flex-1 py-2 pl-10 pr-6 bg-neutral-600 rounded mdmax:pl-3 mdmax:pr-3" :class="{ arrow: !isMobile }">
        <p class="text-sm font-medium text-white">{{ $t('order.createOrder.stepTwo.title') }}</p>
        <p class="text-xs text-white">{{ $t('order.createOrder.stepTwo.desc') }}</p>
      </div>
      <div class="flex-1 py-2 pl-10 pr-6 bg-primary-100 rounded mdmax:pl-3 mdmax:pr-3" :class="{ arrow: !isMobile }">
        <p class="text-sm font-medium ">{{ $t('order.createOrder.stepThree.title') }}</p>
        <p class="text-xs text-neutral-500">{{ $t('order.createOrder.stepThree.desc') }}</p>
      </div>
    </div>
    <div class="flex items-stretch bg-white shadow-border-b mdmax:flex-col">
      <div class="w-1/2 shadow-border-r p-6 mdmax:w-full mdmax:px-4">
        <p class="font-bold mb-4">{{ $t('order.createOrder.stepThree.detail') }}</p>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.shippingDestination') }}</p>
          <p class="text-sm">{{ shippingInfo?.hub?.destination?.area_display }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.serviceType') }}</p>
          <p class="text-sm">{{ logisticInfo?.logistic?.package?.name }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.logistic') }}</p>
          <p class="text-sm">{{ logisticInfo?.logistic?.serviceName }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.goodsCategory') }}</p>
          <p class="text-sm">{{ goodsCategory }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.weight') }}</p>
          <p class="text-sm">{{ calculatedShippingWeight }} kg</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="logisticInfo?.pickup">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.pickupService') }}</p>
          <p class="text-sm">{{ logisticInfo?.pickup?.name }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="logisticInfo?.pickup">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.pickupTime') }}</p>
          <p class="text-sm">{{ pickupTime }} WIB</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="logisticInfo?.selectedPODService">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.pod') }}</p>
          <p class="text-sm">{{ $t('order.createOrder.stepThree.podExist') }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="insurance">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.goodsValue') }}</p>
          <p class="text-sm">{{ goodsValue | currencyFormat }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.shippingCost') }}</p>
          <p class="text-sm">{{ logisticInfo?.logistic?.package?.shipment_cost | currencyFormat }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="logisticInfo?.selectedPODService">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.shippingCostPod') }}</p>
          <p class="text-sm">{{ logisticInfo?.selectedPODService?.shipment_cost | currencyFormat }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="logisticInfo?.pickup">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.pickupCost') }}</p>
          <p class="text-sm">{{ logisticInfo?.pickup?.shipment_cost | currencyFormat }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="logisticInfo?.logistic?.package?.discount_amount">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.shippingCostDiscount') }}</p>
          <p class="text-sm">{{ logisticInfo?.logistic?.package?.discount_amount | currencyFormat }}</p>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b" v-if="insurance">
          <p class="text-sm font-medium">{{ $t('order.createOrder.stepThree.insuranceCost') }}</p>
          <p class="text-sm">{{ insuranceCost | currencyFormat }}</p>
        </div>
        <!-- Voucher Section -->
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <div v-if="!selectedVoucher">
            <button
              @click="openVoucherModal"
              class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-500 border border-primary-500 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                />
              </svg>
              Pilih Voucher
            </button>
          </div>
          <div v-else class="flex items-center gap-2 flex-1">
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900">{{ selectedVoucher.name }}</p>
              <p class="text-xs text-green-600">Diskon {{ selectedVoucher.discount_percentage }}%</p>
            </div>
            <div class="flex items-center gap-2">
              <p class="text-sm text-green-600 font-medium">-{{ discountAmount | currencyFormat }}</p>
              <button @click="selectedVoucher = null" class="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div class="mb-6 flex justify-between items-center pb-2 border-b">
          <p class="text-sm font-bold">{{ $t('order.createOrder.stepThree.totalCost') }}</p>
          <p class="text-sm font-bold" v-if="selectedVoucher">
            {{ finalTotalCost | currencyFormat }}
          </p>
          <p class="text-sm font-bold" v-else-if="logisticInfo?.selectedPODService">
            {{ (logisticInfo?.logistic?.package?.final_cost + logisticInfo?.selectedPODService?.shipment_cost + insuranceCost) | currencyFormat }}
          </p>
          <p class="text-sm font-bold" v-else>
            {{ (logisticInfo?.logistic?.package?.final_cost + insuranceCost) | currencyFormat }}
          </p>
        </div>
      </div>
      <div class="w-1/2 p-6 mdmax:w-full mdmax:px-4">
        <p class="font-bold mb-4">{{ $t('order.createOrder.stepThree.senderInformation') }}</p>
        <div class="border border-neutral-300 p-4 bg-neutral-200 rounded-md mb-6">
          <p class="font-medium text-xs mb-4">{{ $t('order.createOrder.stepThree.senderAddress') }}</p>
          <p class="font-bold mb-1">{{ shippingInfo?.sender?.sender_name }}</p>
          <p class="text-xs text-neutral-500">{{ shippingInfo?.sender?.address }}</p>
          <p class="bg-white p-2 mt-4" v-if="shippingInfo?.sender?.detail_address">
            <span class="block text-xs font-medium mb-1 text-neutral-500">{{ $t('order.createOrder.stepThree.details') }}</span>
            <span class="text-xs text-neutral-500">{{ shippingInfo?.sender?.detail_address }}</span>
          </p>
        </div>
        <div v-if="logisticInfo?.selectedPODService" class="mb-6 flex items-center gap-4 w-full">
          <div class="text-sm w-full p-4 flex items-center gap-2 bg-secondary-100 text-neutral-700 rounded-md">
            <Info width="24" height="24" color="neutral-700" />{{ $t('order.createOrder.stepThree.podNote') }}
          </div>
        </div>
        <p class="font-bold mb-4">{{ $t('order.createOrder.stepThree.receiverInformation') }}</p>
        <div class="border border-neutral-300 p-4 bg-neutral-200 rounded-md mb-6">
          <p class="font-medium text-xs mb-4">{{ $t('order.createOrder.stepThree.receiverAddress') }}</p>
          <p class="font-bold mb-1">{{ shippingInfo?.receiver?.name }}</p>
          <p class="text-xs text-neutral-500">{{ shippingInfo?.receiver?.addressDetail }}, {{ shippingInfo?.hub.destination.village }}, {{ shippingInfo?.hub.destination.area_display }}.</p>
          <!-- <p class="bg-white p-2 mt-4" v-if="shippingInfo?.receiver?.addressDetail">
            <span class="block text-xs font-medium mb-1 text-neutral-500">Details</span>
            <span class="text-xs text-neutral-500">{{ shippingInfo?.receiver?.addressDetail }}</span>
          </p> -->
          <div class="flex justify-between items-center mt-4">
            <p class="text-xs font-medium">{{ $t('order.createOrder.stepThree.itemWeight') }}</p>
            <p class="text-sm text-neutral-500">{{ shippingInfo?.receiver?.weight }} kg</p>
          </div>
          <div class="flex justify-between items-center mt-2" v-if="volumetricWeight > 0">
            <p class="text-xs font-medium text-neutral-500">Volume Paket ({{ shippingInfo?.package?.dimension?.replace(/x/g, ' x ') }} cm)</p>
            <p class="text-sm text-neutral-500">{{ volumetricWeight }} kg</p>
          </div>
        </div>
        <div class="flex gap-8 justify-center" v-if="!user.is_upgraded">
          <div class="border rounded-md p-8 flex-1">
            <label class="flex gap-4">
              <input type="radio" name="paymentType" id="paymentType1" value="qris" v-model="paymentType" />
              <img src="@/assets/images/icons/qris.svg" class="h-1/3 w-1/3" />
            </label>
          </div>
          <div class="border rounded-md p-8 flex-1">
            <label>
              <input
                :disabled="
                  logisticInfo?.logistic?.package?.final_cost + insuranceCost < 80000 ||
                    (logisticInfo?.selectedPODService && logisticInfo?.logistic?.package?.final_cost + logisticInfo?.selectedPODService?.shipment_cost + insuranceCost < 80000)
                "
                type="radio"
                name="paymentType"
                id="paymentType2"
                value="transfer"
                v-model="paymentType"
              />
              Bank Transfer
            </label>
            <p class="text-red-500 text-xs" v-if="logisticInfo?.logistic?.package?.final_cost + insuranceCost < 80000">
              To use this payment, the minimum payment is IDR 80.000
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center p-6 justify-end bg-white gap-4 mdmax:flex-col">
      <div class="mdmax:order-2">
        <Button :buttonText="$t('order.createOrder.stepThree.back')" type="secondary" @action="goBack('logistic')" />
      </div>
      <div class="mdmax:order-1">
        <Button :disabled="(!user.is_upgraded && !paymentType) || isLionParcelMissingCoord" :buttonText="$t('order.createOrder.stepThree.pay')" type="primary" @action="user.is_upgraded ? confirmationOrder() : submitOrder()" />
      </div>
      <div class="mdmax:order-1">
        <Button :disabled="(!user.is_upgraded && !paymentType) || isLionParcelMissingCoord" :buttonText="$t('order.createOrder.stepThree.draft')" type="primary" @action="saveDraft()" />
      </div>
    </div>
    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-lg p-6 w-96">
        <h2 class="text-lg font-bold mb-4">Konfirmasi Pembayaran</h2>
        <p class="text-sm mb-6">Apakah anda ingin melanjutkan proses pembayaran?</p>
        <div class="flex justify-end gap-4">
          <button @click="cancelVIPPayment" class="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400">Batal</button>
          <button @click="confirmVIPPayment" class="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">Ya, Bayar</button>
        </div>
      </div>
    </div>
    <!-- Notify Modal -->
    <div v-if="showNotifyModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-lg p-6 w-96">
        <h2 class="text-lg font-bold mb-4">Berhasil Dipesan</h2>
        <p class="text-sm mb-6">Pembayaran ini tercatat di Invoice Pembayaran</p>
        <div class="flex justify-center gap-4">
          <button @click="confirmNotifyPayment" class="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">OK</button>
        </div>
      </div>
    </div>
    <!-- Voucher Modal -->
    <div v-if="showVoucherModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="closeVoucherModal">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col" style="max-height: 600px;">
        <!-- Modal Header -->
        <div class="flex justify-between items-center px-5 py-4 border-b bg-white rounded-t-lg flex-shrink-0">
          <h2 class="text-lg font-bold text-gray-800">Pilih Voucher</h2>
          <button @click="closeVoucherModal" class="text-gray-400 hover:text-gray-700 transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <!-- Modal Body -->
        <div class="overflow-y-auto flex-1 p-4" style="min-height: 0;">
          <!-- Search Input -->
          <div class="mb-4">
            <div class="relative">
              <input
                v-model="voucherSearchQuery"
                @input="searchVouchers"
                type="text"
                placeholder="Cari kode voucher..."
                class="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <svg xmlns="http://www.w3.org/2000/svg" class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div v-if="vouchers.length === 0" class="text-center py-8 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
              />
            </svg>
            <p class="text-lg font-medium">Tidak ada voucher tersedia</p>
            <p class="text-sm mt-2">Voucher akan muncul di sini ketika tersedia</p>
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="voucher in vouchers"
              :key="voucher.id"
              @click="selectVoucher(voucher)"
              class="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer transition-all hover:border-primary-400 hover:shadow-sm"
              :class="{ 'border-primary-500 bg-primary-50': selectedVoucher?.id === voucher.id }"
            >
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0">
                  <div class="w-10 h-10 bg-cyan-100 rounded flex items-center justify-center overflow-hidden">
                    <img
                      v-if="voucher.delivery_service && voucher.delivery_service.logo"
                      :src="voucher.delivery_service.logo"
                      :alt="voucher.delivery_service.name"
                      class="w-full h-full object-contain"
                      @error="$event.target.style.display = 'none'"
                    />
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                    </svg>
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="font-semibold text-base text-gray-900 mb-1">{{ voucher.name }}</h3>
                  <p class="text-xs text-gray-600 mb-2 line-clamp-2" v-if="voucher.delivery_service">
                    {{ voucher.delivery_service.name }}
                  </p>
                  <div class="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span>Kode: {{ voucher.voucher_code }}</span>
                    <span>•</span>
                    <span>Berlaku hingga: {{ formatDate(voucher.end_date) }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded"> Diskon {{ voucher.discount_percentage }}% </span>
                    <span class="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded"> Sisa kuota: {{ voucher.available_quota }} </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Map Modal -->
    <div v-if="showMapModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="!isLionParcelMissingCoord ? showMapModal = false : null">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col" style="max-height: 90vh;">
        <div class="flex justify-between items-center px-5 py-4 border-b bg-white rounded-t-lg">
          <h2 class="text-lg font-bold text-gray-800">Lokasi Penjemputan</h2>
          <button v-if="!isLionParcelMissingCoord" @click="showMapModal = false" class="text-gray-400 hover:text-gray-700 transition-colors p-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="overflow-y-auto flex-1 p-6 relative">
          <div class="relative">
            <!-- Search Input -->
            <div class="absolute top-2 left-2 right-2 z-10">
              <div class="relative">
                <input
                  type="text"
                  v-model="placeSearch"
                  @input="searchPlaces"
                  @focus="showPlaceSuggestions = true"
                  :placeholder="$t('address.add.searchLocation') || 'Cari lokasi...'"
                  class="w-full px-4 py-2 pr-10 rounded-lg border border-neutral-300 bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div class="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Search class="w-5 h-5 text-neutral-400" />
                </div>
                <!-- Autocomplete Suggestions -->
                <transition name="fade">
                  <div
                    v-if="showPlaceSuggestions && placeSuggestions.length > 0"
                    class="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg border border-neutral-200 max-h-60 overflow-y-auto z-50"
                  >
                    <div
                      v-for="(place, index) in placeSuggestions"
                      :key="index"
                      @click="selectPlace(place)"
                      class="px-4 py-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
                    >
                      <div class="text-sm font-medium text-neutral-900">{{ place.description }}</div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
            <!-- Map -->
            <GmapMap
              :center="mapCenter"
              :zoom="mapZoom"
              map-type-id="roadmap"
              class="w-full h-96 rounded-lg overflow-hidden border border-neutral-300"
              :options="{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true
              }"
              @click="updateMarkerPosition"
            >
              <GmapMarker :position="mapCenter" :draggable="true" @dragend="updateMarkerPosition" />
            </GmapMap>
            <!-- Pilih Lokasi Button -->
            <button
              @click="confirmLocation"
              :disabled="isConfirmingLocation"
              class="mt-4 w-full flex items-center justify-center gap-2 text-white px-4 py-3 rounded-lg shadow-lg font-bold transition-colors disabled:opacity-70"
              style="background-color: #61d7d0;"
            >
              <svg v-if="!isConfirmingLocation" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isConfirmingLocation ? ($t('address.add.loading') || 'Loading...') : ($t('address.add.selectLocation') || 'Pilih Lokasi') }}
            </button>
            <!-- Latitude and Longitude Display -->
            <div class="mt-2 text-sm text-neutral-600 text-center">
              <span class="font-medium">{{ $t('address.add.coordinates') || 'Koordinat' }}:</span> {{ mapCenter.lat.toFixed(6) }}, {{ mapCenter.lng.toFixed(6) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { formatTime } from '@/util'
import { currencyFormat } from '@/util'
import { onBeforeMount } from 'vue'
export default {
  filters: {
    currencyFormat
  },
  components: {
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    Info: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Info'),
    Search: () => import('@/components/Icons/Search')
  },
  data() {
    return {
      draftShippingOrder: null,
      shippingInfo: null,
      logisticInfo: null,
      goodsCategory: null,
      paymentLink: null,
      insurance: false,
      goodsValue: 0,
      insuranceCost: 0,
      showConfirmModal: false,
      showNotifyModal: false,
      showVoucherModal: false,
      selectedVoucher: null,
      voucherSearchQuery: '',
      searchTimeout: null,
      paymentType: '',
      localStorageAsync: {
        set: function(key, value) {
          return Promise.resolve().then(function() {
            localStorage.setItem(key, value)
          })
        },
        get: function(key) {
          return Promise.resolve().then(function() {
            return localStorage.getItem(key)
          })
        }
      },
      showMapModal: false,
      mapCenter: {
        lat: -6.2,
        lng: 106.816666
      },
      mapZoom: 15,
      placeSearch: '',
      placeSuggestions: [],
      showPlaceSuggestions: false,
      placeSearchDebounce: null,
      isConfirmingLocation: false
    }
  },
  async mounted() {
    const shipping = localStorage.getItem('order_shipping')

    if (shipping) {
      this.shippingInfo = JSON.parse(shipping)
    } else {
      await this.getDraftOrderDetail({
        draftOrderId: 4
      }).then(response => {
        this.draftShippingOrder = response.data.data
        this.shippingInfo = JSON.parse(this.draftShippingOrder.order_shipping)
      })
    }
    const logistic = localStorage.getItem('order_logistic')
    if (logistic) {
      this.logisticInfo = JSON.parse(logistic)
      this.insurance = this.logisticInfo.insurance
      this.insuranceCost = this.logisticInfo.insuranceCost
      this.goodsValue = this.logisticInfo.goodsValue
      if (!this.logisticInfo.insurance) {
        this.insurance = false
        this.insuranceCost = 0
        this.goodsValue = 0
      }
    }
    
    this.checkLionParcelLocation()
    this.getGoodsCategoryList()
    this.fetchVouchers()
    
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  computed: {
    ...mapGetters('auth', ['userData']),
    ...mapGetters('master', ['goodsCategories']),
    ...mapGetters('order', ['draftOrderDetail']),
    ...mapGetters('common', ['isMobile']),
    ...mapGetters('merchant', ['vouchers']),
    pickupTime() {
      let today = new Date()
      today.setHours(today.getHours() + 1)
      return formatTime(today)
    },
    formattedPickupTime() {
      let today = new Date()
      today.setHours(today.getHours() + 1)
      return today.toISOString()
    },
    totalCost() {
      return this.logisticInfo?.pickup
        ? this.logisticInfo?.logistic?.package?.shipment_cost + this.logisticInfo?.pickup?.shipment_cost + this.insuranceCost
        : this.logisticInfo?.logistic?.package?.shipment_cost + this.insuranceCost
    },
    discountAmount() {
      if (!this.selectedVoucher || !this.selectedVoucher.discount_percentage) {
        return 0
      }
      const calculatedDiscount = (this.totalCost * this.selectedVoucher.discount_percentage) / 100

      // Apply max discount limit if exists
      if (this.selectedVoucher.max_discount_amount && calculatedDiscount > this.selectedVoucher.max_discount_amount) {
        return this.selectedVoucher.max_discount_amount
      }

      return calculatedDiscount
    },
    finalTotalCost() {
      return this.totalCost - this.discountAmount
    },
    user() {
      return this.userData?.user
    },
    isLionParcelMissingCoord() {
      const isLion = this.logisticInfo?.logistic?.serviceName?.toLowerCase().includes('lion')
      const coord = this.shippingInfo?.sender?.location_coordinate
      const isMissing = !coord || coord === 'lat,long' || coord === ''
      return isLion && isMissing
    },
    calculatedShippingWeight() {
      let weight = parseFloat(this.shippingInfo?.receiver?.weight || 0)
      if (this.shippingInfo?.package?.dimension) {
        const dims = this.shippingInfo.package.dimension.split('x')
        if (dims.length === 3) {
          const p = parseFloat(dims[0])
          const l = parseFloat(dims[1])
          const t = parseFloat(dims[2])
          const volWeight = (p * l * t) / 6000
          weight = Math.max(weight, volWeight)
        }
      }
      return Math.ceil(weight)
    },
    volumetricWeight() {
      if (this.shippingInfo?.package?.dimension) {
        const dims = this.shippingInfo.package.dimension.split('x')
        if (dims.length === 3) {
          const p = parseFloat(dims[0])
          const l = parseFloat(dims[1])
          const t = parseFloat(dims[2])
          return Math.ceil((p * l * t) / 6000)
        }
      }
      return 0
    }
  },
  methods: {
    ...mapActions('common', ['showLoading', 'hideLoading', 'setToast']),
    checkLionParcelLocation() {
      if (this.shippingInfo && this.logisticInfo) {
        if (this.isLionParcelMissingCoord) {
          this.showMapModal = true
          this.getUserLocation()
        }
      }
    },
    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            this.mapCenter = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }
          },
          error => {
            console.error('Error getting location:', error)
          }
        )
      }
    },
    updateMarkerPosition(event) {
      this.mapCenter = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
      this.showPlaceSuggestions = false
    },
    searchPlaces() {
      clearTimeout(this.placeSearchDebounce)
      if (!this.placeSearch || this.placeSearch.length < 3) {
        this.placeSuggestions = []
        return
      }
      this.placeSearchDebounce = setTimeout(() => {
        if (!window.google || !window.google.maps) return
        const service = new window.google.maps.places.AutocompleteService()
        service.getPlacePredictions(
          {
            input: this.placeSearch,
            componentRestrictions: { country: 'id' }
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              this.placeSuggestions = predictions
            } else {
              this.placeSuggestions = []
            }
          }
        )
      }, 300)
    },
    selectPlace(place) {
      this.placeSearch = place.description
      this.showPlaceSuggestions = false
      this.placeSuggestions = []
      if (window.google && window.google.maps) {
        const service = new window.google.maps.places.PlacesService(document.createElement('div'))
        service.getDetails(
          {
            placeId: place.place_id,
            fields: ['geometry']
          },
          (placeDetails, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && placeDetails.geometry) {
              this.mapCenter = {
                lat: placeDetails.geometry.location.lat(),
                lng: placeDetails.geometry.location.lng()
              }
              this.mapZoom = 17
            }
          }
        )
      }
    },
    confirmLocation() {
      this.isConfirmingLocation = true
      const newCoord = `${this.mapCenter.lat},${this.mapCenter.lng}`
      
      if (!this.shippingInfo.sender) {
        this.shippingInfo.sender = {}
      }
      
      this.shippingInfo.sender.location_coordinate = newCoord
      localStorage.setItem('order_shipping', JSON.stringify(this.shippingInfo))
      
      this.showMapModal = false
      this.isConfirmingLocation = false
      
      this.setToast({
        options: {
          type: 'success',
          duration: 3000,
          primaryText: 'Lokasi berhasil disimpan',
          secondaryText: `Koordinat: ${this.mapCenter.lat.toFixed(6)}, ${this.mapCenter.lng.toFixed(6)}`,
          callback: () => {}
        }
      })
    },
    handleClickOutside(event) {
      const placeSearchContainer = event.target.closest('.relative')
      if (!placeSearchContainer || !placeSearchContainer.querySelector('input[type="text"]')) {
        this.showPlaceSuggestions = false
      }
    },
    ...mapActions('master', ['getGoodsCategories']),
    ...mapActions('merchant', ['createOrder', 'getVouchers']),
    ...mapActions('order', ['createDraftOrder', 'getDraftOrderDetail', 'updateDraftOrder', 'deleteDraftOrder']),
    toggleIframe() {
      this.visibleIframe = !this.visibleIframe
    },
    getGoodsCategoryList() {
      this.getGoodsCategories().then(() => {
        this.goodsCategory = this.goodsCategories.filter(item => item.code === this.shippingInfo?.hub?.goodsCategory)[0]?.name
      })
    },
    fetchVouchers(voucherCode = '') {
      const params = {}
      // Optionally filter by delivery service ID if available
      if (this.logisticInfo?.logistic?.package?.delivery_service_id) {
        params.delivery_service_id = this.logisticInfo.logistic.package.delivery_service_id
      }
      // Add delivery service package ID if available
      if (this.logisticInfo?.logistic?.package?.id) {
        params.delivery_service_package_id = this.logisticInfo.logistic.package.id
      }
      // Add voucher_code parameter if provided
      if (voucherCode) {
        params.voucher_code = voucherCode
      }
      this.getVouchers({ params }).catch(error => {
        console.error('Error fetching vouchers:', error)
      })
    },
    searchVouchers() {
      // Debounce search to avoid too many API calls
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchVouchers(this.voucherSearchQuery)
      }, 500)
    },
    openVoucherModal() {
      this.voucherSearchQuery = ''
      this.fetchVouchers() // Reset to show all vouchers
      this.showVoucherModal = true
    },
    closeVoucherModal() {
      this.showVoucherModal = false
      this.voucherSearchQuery = ''
    },
    goBack(target) {
      this.showLoading()
      setTimeout(() => {
        this.$router.push(`/order/step/${target}${this.$route.query.draft ? `?draft=${this.$route.query.draft}` : ''}`)
        this.hideLoading()
      }, 1000)
    },
    confirmationOrder() {
      this.showConfirmModal = true // show the modal instead of directly submitting
    },

    confirmVIPPayment() {
      this.showConfirmModal = false
      this.showNotifyModal = true
    },

    cancelVIPPayment() {
      this.showConfirmModal = false
    },

    confirmNotifyPayment() {
      this.showNotifyModal = false
      this.submitOrder() // reuse your existing order submit function
    },
    formatCurrency(value) {
      return currencyFormat(value)
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const options = { day: 'numeric', month: 'short', year: 'numeric' }
      return date.toLocaleDateString('id-ID', options)
    },
    selectVoucher(voucher) {
      this.selectedVoucher = voucher
      const discountAmount = (this.totalCost * voucher.discount_percentage) / 100
      this.showVoucherModal = false
      this.voucherSearchQuery = ''
      this.setToast({
        options: {
          type: 'success',
          duration: 3000,
          primaryText: 'Voucher Berhasil Diterapkan',
          secondaryText: `Diskon ${voucher.discount_percentage}% - Hemat ${this.formatCurrency(discountAmount)}`,
          callback: () => {}
        }
      })
    },
    applyVoucher() {
      if (this.selectedVoucher) {
        const discountAmount = (this.totalCost * this.selectedVoucher.discount_percentage) / 100
        this.showVoucherModal = false
        this.voucherSearchQuery = ''
        this.setToast({
          options: {
            type: 'success',
            duration: 3000,
            primaryText: 'Voucher Berhasil Diterapkan',
            secondaryText: `Diskon ${this.selectedVoucher.discount_percentage}% - Hemat ${this.formatCurrency(discountAmount)}`,
            callback: () => {}
          }
        })
      }
    },
    getDraftOrderInfo() {
      this.showLoading()
      this.getDraftOrderDetail({
        draftOrderId: 4
      }).then(response => {
        this.draftShippingOrder = response.data.data
        this.hideLoading()
      })

      return this.draftShippingOrder
    },
    submitOrder() {
      const payloads = {
        is_advance_pickup: this.shippingInfo.is_advance_pickup,
        is_require_pod: this.shippingInfo.is_require_pod,
        sender: {
          name: this.shippingInfo?.sender?.sender_name,
          phone_number: this.shippingInfo?.sender?.sender_phone,
          location_code: this.shippingInfo?.hub?.origin?.code,
          location_coordinate: this.shippingInfo?.sender?.location_coordinate,
          address: this.shippingInfo?.sender?.address,
          detail_address: this.shippingInfo?.sender?.detail_address
        },
        service_id: this.logisticInfo?.logistic?.package?.delivery_service_id,
        service_type: this.logisticInfo?.logistic?.package?.service_type,
        service_category: 'REG',
        service_package_id: this.logisticInfo?.logistic?.package?.id,
        order_type: 'DELPKG',
        packages: [
          {
            category: this.shippingInfo?.hub?.goodsCategory,
            dimension: this.shippingInfo?.package.dimension,
            dimension_type: this.shippingInfo?.package.code,
            weight: parseFloat(this.shippingInfo?.receiver?.weight || 0),
            has_dangerous_goods: false,
            has_glass: false,
            goods_description: '',
            has_high_value: false,
            has_document: false,
            has_electronic: false,
            receiver: {
              name: this.shippingInfo?.receiver?.name,
              phone_number: this.shippingInfo?.receiver?.phone,
              location_code: this.shippingInfo?.hub?.destination?.code,
              location_coordinate: `${this.shippingInfo?.receiver?.location?.latitude},${this.shippingInfo?.receiver?.location?.longitude}`,
              address: this.shippingInfo?.receiver?.addressDetail,
              detail_address: ''
            }
          }
        ],
        pickup_detail: {
          service_id: this.logisticInfo?.pickup?.delivery_service_id || '',
          service_package_id: this.logisticInfo?.pickup?.id || '',
          pickup_time: this.logisticInfo?.pickup ? this.formattedPickupTime : ''
        },
        transit_detail: {
          transit_place_id: this.logisticInfo?.nearest_hub?.id || '',
          place_location_code: this.logisticInfo?.nearest_hub?.area_code,
          place_location_coords: this.logisticInfo?.nearest_hub ? `${this.logisticInfo?.nearest_hub?.coord_lat}, ${this.logisticInfo?.nearest_hub?.coord_lng}` : ''
        },
        goods_value: this.goodsValue,
        insurance_cost: this.insuranceCost,
        enabled_payments: this.paymentType
      }

      // Add voucher information if voucher is selected
      if (this.selectedVoucher) {
        payloads.voucher_id = this.selectedVoucher.id
        payloads.voucher_amount = this.discountAmount
      }

      if (this.shippingInfo.is_advance_pickup) {
        payloads.pickup_slot_id = this.shippingInfo?.pickup_slot_id?.id
        payloads.pickup_date = this.shippingInfo.pickup_date
      }

      if (this.shippingInfo.is_require_pod) {
        payloads.pod = {
          service_package_id: this.logisticInfo?.selectedPODService?.id,
          service_id: this.logisticInfo?.logistic?.package?.delivery_service_id
        }
      }

      const order = localStorage.getItem('order')
      if (order) {
        const orderResponse = JSON.parse(order)
        if (orderResponse.is_advance_payment) {
          this.handlePay(orderResponse)
        } else {
          this.$router.push(`/order/detail/${orderResponse.order_id}`)
        }
      } else {
        this.showLoading()
        this.createOrder({
          payloads: payloads
        }).then(res => {
          if (this.$route.query.draft) {
            this.deleteDraftOrder({
              draftOrderId: this.$route.query.draft
            })
          }
          this.hideLoading()
          if (res.data.data.is_advance_payment) {
            this.handlePay(res.data.data)
          } else {
            this.$router.push(`/order/detail/${res.data.data.order_id}`)
          }
        })
      }
    },
    saveDraft() {
      const order_shipping = localStorage.getItem('order_shipping')
      const order_logistic = localStorage.getItem('order_logistic')

      const payloads = {
        is_advance_pickup: this.shippingInfo.is_advance_pickup,
        is_require_pod: this.shippingInfo.is_require_pod,
        sender: {
          name: this.shippingInfo?.sender?.sender_name,
          phone_number: this.shippingInfo?.sender?.sender_phone,
          location_code: this.shippingInfo?.hub?.origin?.code,
          location_coordinate: this.shippingInfo?.sender?.location_coordinate,
          address: this.shippingInfo?.sender?.address,
          detail_address: this.shippingInfo?.sender?.detail_address
        },
        service_id: this.logisticInfo?.logistic?.package?.delivery_service_id,
        service_type: this.logisticInfo?.logistic?.package?.service_type,
        service_category: 'REG',
        service_package_id: this.logisticInfo?.logistic?.package?.id,
        order_type: 'DELPKG',
        packages: [
          {
            category: this.shippingInfo?.hub?.goodsCategory,
            dimension: this.shippingInfo?.package.dimension,
            dimension_type: this.shippingInfo?.package.code,
            weight: parseInt(this.shippingInfo?.receiver?.weight),
            has_dangerous_goods: false,
            has_glass: false,
            goods_description: '',
            has_high_value: false,
            has_document: false,
            has_electronic: false,
            receiver: {
              name: this.shippingInfo?.receiver?.name,
              phone_number: this.shippingInfo?.receiver?.phone,
              location_code: this.shippingInfo?.hub?.destination?.code,
              location_coordinate: `${this.shippingInfo?.receiver?.location?.latitude},${this.shippingInfo?.receiver?.location?.longitude}`,
              address: this.shippingInfo?.receiver?.addressDetail,
              detail_address: ''
            }
          }
        ],
        pickup_detail: {
          service_id: this.logisticInfo?.pickup?.delivery_service_id || '',
          service_package_id: this.logisticInfo?.pickup?.id || '',
          pickup_time: this.logisticInfo?.pickup ? this.formattedPickupTime : ''
        },
        transit_detail: {
          transit_place_id: this.logisticInfo?.nearest_hub?.id || '',
          place_location_code: this.logisticInfo?.nearest_hub?.area_code,
          place_location_coords: this.logisticInfo?.nearest_hub ? `${this.logisticInfo?.nearest_hub?.coord_lat}, ${this.logisticInfo?.nearest_hub?.coord_lng}` : ''
        },
        goods_value: this.goodsValue,
        insurance_cost: this.insuranceCost,
        enabled_payments: this.paymentType,
        order_shipping: order_shipping,
        order_logistic: order_logistic
      }

      this.showLoading()

      if (this.$route.query.draft) {
        this.updateDraftOrder({
          draftOrderId: this.$route.query.draft,
          payloads: payloads
        }).then(() => {
          this.hideLoading()
          this.$router.push(`/order`)
        })
      } else {
        this.createDraftOrder({
          payloads: payloads
        }).then(() => {
          this.hideLoading()
          this.$router.push(`/order`)
        })
      }
    },
    handlePay(res) {
      const self = this
      const response = res
      this.localStorageAsync.set('order', JSON.stringify(response))
      this.localStorageAsync.set('payment_finish_url', JSON.stringify(response.payment_finish_url))
      this.paymentLink = response.payment_redirect_url
      window.snap.pay(response.payment_token, {
        onSuccess: function(result) {
          console.log('success')
          console.log(result)
          self.showLoading()
          setTimeout(() => {
            self.$router.push(`/order`)
            self.hideLoading()
          }, 1000)
        },
        onPending: function(result) {
          console.log('pending')
          console.log(result)
          self.showLoading()
          setTimeout(() => {
            self.$router.push(`/order`)
            self.hideLoading()
          }, 1000)
        },
        onError: function(result) {
          console.log('error')
          console.log(result)
          self.showLoading()
          setTimeout(() => {
            self.$router.push(`/order`)
            self.hideLoading()
          }, 1000)
        },
        onClose: function() {
          console.log('customer closed the popup without finishing the payment')
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.arrow {
  margin: 0 0 0 -12px;
  -webkit-clip-path: polygon(20px 50%, 0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%);
  clip-path: polygon(20px 50%, 0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%);

  &:first-child {
    -webkit-clip-path: polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%);
    clip-path: polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%);
  }

  &:last-child {
    -webkit-clip-path: polygon(20px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%);
    clip-path: polygon(20px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%);
  }
}

.stepper {
  &::before {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    content: '';
  }
}

.place {
  &::v-deep input {
    padding: 12px 12px 12px 44px;
    outline: none;
    border-radius: 8px;
    border: 1px solid #ced5de;
    width: 100%;
  }
}

.custom-select {
  &::v-deep .vs__dropdown-toggle {
    padding: 12px;
    border-radius: 8px;
  }

  &::v-deep .vs__actions {
    padding: 0;
  }

  &::v-deep .vs__search,
  &::v-deep .vs__selected {
    padding: 0;
    margin: 0;
  }

  &::v-deep .vs__search::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: #748497;
    opacity: 1;
    /* Firefox */
  }
}
</style>
